import { useEffect, useRef, useState } from "react";
import { useSpotlight } from "../context/SpotlightContext";

/**
 * Positioned tooltip that displays announcement content next to the spotlight target.
 * Adapts position to stay within viewport bounds.
 */
const SpotlightTooltip = () => {
  const { currentAnnouncement, targetRect, dismiss, isSpotlightActive } =
    useSpotlight();
  const tooltipRef = useRef(null);
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const [actualPosition, setActualPosition] = useState("bottom");

  useEffect(() => {
    if (!isSpotlightActive || !targetRect || !tooltipRef.current) return;

    const tooltip = tooltipRef.current;
    const tooltipRect = tooltip.getBoundingClientRect();
    const preferredPosition = currentAnnouncement?.position || "bottom";
    const padding = 16;
    const arrowOffset = 12;

    // Calculate position based on preference
    const positions = {
      top: {
        top: targetRect.top - tooltipRect.height - arrowOffset,
        left: targetRect.left + targetRect.width / 2 - tooltipRect.width / 2,
      },
      bottom: {
        top: targetRect.bottom + arrowOffset,
        left: targetRect.left + targetRect.width / 2 - tooltipRect.width / 2,
      },
      left: {
        top: targetRect.top + targetRect.height / 2 - tooltipRect.height / 2,
        left: targetRect.left - tooltipRect.width - arrowOffset,
      },
      right: {
        top: targetRect.top + targetRect.height / 2 - tooltipRect.height / 2,
        left: targetRect.right + arrowOffset,
      },
    };

    let pos = positions[preferredPosition];
    let finalPosition = preferredPosition;

    // Check if position fits in viewport, try alternatives if not
    const fitsInViewport = (p, posName) => {
      return (
        p.top >= padding &&
        p.left >= padding &&
        p.top + tooltipRect.height <= window.innerHeight - padding &&
        p.left + tooltipRect.width <= window.innerWidth - padding
      );
    };

    if (!fitsInViewport(pos, preferredPosition)) {
      // Try opposite position first
      const opposites = {
        top: "bottom",
        bottom: "top",
        left: "right",
        right: "left",
      };
      const opposite = opposites[preferredPosition];

      if (fitsInViewport(positions[opposite], opposite)) {
        pos = positions[opposite];
        finalPosition = opposite;
      } else {
        // Try other positions
        for (const [posName, posValue] of Object.entries(positions)) {
          if (fitsInViewport(posValue, posName)) {
            pos = posValue;
            finalPosition = posName;
            break;
          }
        }
      }
    }

    // Clamp to viewport bounds
    pos.top = Math.max(
      padding,
      Math.min(pos.top, window.innerHeight - tooltipRect.height - padding),
    );
    pos.left = Math.max(
      padding,
      Math.min(pos.left, window.innerWidth - tooltipRect.width - padding),
    );

    setPosition(pos);
    setActualPosition(finalPosition);
  }, [isSpotlightActive, targetRect, currentAnnouncement]);

  if (!isSpotlightActive || !currentAnnouncement) {
    return null;
  }

  const arrowClasses = {
    top: "top-full left-1/2 -translate-x-1/2 border-l-8 border-r-8 border-t-8 border-transparent border-t-white",
    bottom:
      "bottom-full left-1/2 -translate-x-1/2 border-l-8 border-r-8 border-b-8 border-transparent border-b-white",
    left: "left-full top-1/2 -translate-y-1/2 border-t-8 border-b-8 border-l-8 border-transparent border-l-white",
    right:
      "right-full top-1/2 -translate-y-1/2 border-t-8 border-b-8 border-r-8 border-transparent border-r-white",
  };

  return (
    <div
      ref={tooltipRef}
      className="fixed z-[10002] max-w-sm animate-fade-in"
      style={{
        top: position.top,
        left: position.left,
      }}
    >
      <div className="bg-white rounded-lg shadow-xl border border-border overflow-hidden">
        {/* Header */}
        <div className="px-4 py-3 bg-teal-50 border-b border-teal-100">
          <h3 className="text-lg font-semibold text-teal-800">
            {currentAnnouncement.title}
          </h3>
        </div>

        {/* Content */}
        <div className="px-4 py-3">
          <p className="text-gray-600 text-sm leading-relaxed">
            {currentAnnouncement.description}
          </p>
        </div>

        {/* Footer */}
        <div className="px-4 py-3 bg-gray-50 border-t border-gray-100 flex justify-end">
          <button
            onClick={dismiss}
            className="px-4 py-2 bg-teal-700 text-white text-sm font-medium rounded-lg
                       hover:bg-teal-800 focus:outline-none focus:ring-2 focus:ring-teal-500
                       focus:ring-offset-2 transition-colors"
          >
            Got it
          </button>
        </div>
      </div>

      {/* Arrow */}
      <div className={`absolute w-0 h-0 ${arrowClasses[actualPosition]}`} />

      {/* Animation styles */}
      <style>{`
        @keyframes fade-in {
          from {
            opacity: 0;
            transform: translateY(8px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        .animate-fade-in {
          animation: fade-in 0.3s ease-out;
        }
      `}</style>
    </div>
  );
};

export default SpotlightTooltip;
